import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { refreshSession } from '../services/authService';

// Costanti per la gestione delle sessioni (in millisecondi)
const CHECK_INTERVAL = 60*1000; // Controlla ogni minuto
const AUTO_REFRESH_THRESHOLD = 30 * 60 * 1000; // Auto-refresh quando mancano 30 minuti
const ACTIVITY_REFRESH_COOLDOWN = 5 * 60 * 1000; // Non fare refresh più spesso di ogni 5 minuti

/**
 * Hook personalizzato per gestire il refresh automatico delle sessioni
 * 
 * Features:
 * - Auto-refresh della sessione quando l'utente è attivo
 * - Tracking dell'attività dell'utente
 * - Prevenzione del logout automatico durante l'uso
 */
export const useSessionManager = () => {
  const { isAuthenticated, logout, updateSession, sessionExpiresAt } = useAuth();
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const lastRefreshTime = useRef(0);
  const checkIntervalRef = useRef(null);
  const checkCounter = useRef(0);
  const checkSessionStatusRef = useRef(null);

  const performRefresh = useCallback(async () => {
    if (isRefreshing || !isAuthenticated) {
      return false;
    }

    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime.current;
    
    // SAFETY CHECK: Ensure sessionExpiresAt is valid
    if (!sessionExpiresAt) {
      // Skip dynamic cooldown if we don't have session expiration info
    } else {
      // DYNAMIC COOLDOWN: Shorter cooldown for sessions that are about to expire
      const expirationTime = sessionExpiresAt instanceof Date ? sessionExpiresAt.getTime() : new Date(sessionExpiresAt).getTime();
      const timeUntilExpiration = expirationTime - now;
      
      // SMART COOLDOWN: Scale cooldown based on time remaining
      let dynamicCooldown;
      if (timeUntilExpiration < 2 * 60 * 1000) {
        dynamicCooldown = 5 * 1000; // 5 seconds for sessions with <2 minutes left
      } else if (timeUntilExpiration < 10 * 60 * 1000) {
        dynamicCooldown = 10 * 1000; // 10 seconds for sessions with <10 minutes left
      } else if (timeUntilExpiration < 30 * 60 * 1000) {
        dynamicCooldown = 30 * 1000; // 30 seconds for sessions with <30 minutes left
      } else {
        dynamicCooldown = ACTIVITY_REFRESH_COOLDOWN; // 5 minutes for longer sessions
      }
      
      if (timeSinceLastRefresh < dynamicCooldown) {
        return false;
      }
    }

    // If sessionExpiresAt is not available or cooldown passed, proceed with refresh
    setIsRefreshing(true);
    
    try {
      const response = await refreshSession();
      
      if (response && response.token) {
        // Aggiorna il token nel localStorage
        localStorage.setItem('authToken', response.token);
        
        // Aggiorna la data di scadenza
        if (response.expiresAt) {
          // Update the session in the context, which is the single source of truth
          if (updateSession) {
            updateSession(response.token, response.expiresAt);
          }
        }
        
        lastRefreshTime.current = now;
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      // Se il refresh fallisce, probabilmente la sessione è scaduta
      if (error.response?.status === 401) {
        logout();
      }
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, isAuthenticated, logout, updateSession, sessionExpiresAt]);

  const updateActivity = useCallback(() => {
    const now = Date.now();
    setLastActivity(now);
    
    // ACTIVITY-BASED REFRESH: Check if we need to refresh when user is active
    if (sessionExpiresAt && isAuthenticated) {
      const expirationTime = sessionExpiresAt instanceof Date ? sessionExpiresAt.getTime() : new Date(sessionExpiresAt).getTime();
      const timeUntilExpiration = expirationTime - now;
      const timeSinceLastRefresh = now - lastRefreshTime.current;
      
      // Only refresh if:
      // 1. Session expires within threshold AND
      // 2. We haven't refreshed recently (cooldown) AND  
      // 3. We're not already refreshing
      if (timeUntilExpiration <= AUTO_REFRESH_THRESHOLD && 
          timeSinceLastRefresh >= 10 * 1000 && // 10 second cooldown minimum
          !isRefreshing) {
        performRefresh();
      }
    }
  }, [sessionExpiresAt, isAuthenticated, isRefreshing, performRefresh]);

  const checkSessionStatus = useCallback(async () => {
    const checkId = ++checkCounter.current;

    if (!isAuthenticated || !sessionExpiresAt) {
      return;
    }

    const now = Date.now();
    
    try {
      const expirationTime = sessionExpiresAt instanceof Date ? sessionExpiresAt.getTime() : new Date(sessionExpiresAt).getTime();
      const timeUntilExpiration = expirationTime - now;

      // Se la sessione è già scaduta, logout
      if (timeUntilExpiration <= 0) {
        logout();
        return;
      }

    } catch (error) {
      // Silent error handling
    }
  }, [isAuthenticated, sessionExpiresAt, logout]);

  useEffect(() => {
    checkSessionStatusRef.current = checkSessionStatus;
  }, [checkSessionStatus]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Only create interval if it doesn't exist
    if (!checkIntervalRef.current) {
      checkIntervalRef.current = setInterval(() => {
        if (checkSessionStatusRef.current) {
          checkSessionStatusRef.current();
        }
      }, CHECK_INTERVAL);
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [isAuthenticated]);

  // Effetto per il tracking dell'attività dell'utente
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Throttle per evitare troppi aggiornamenti
    let throttleTimer = null;
    const throttledUpdateActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        updateActivity();
        throttleTimer = null;
      }, 1000); // Aggiorna al massimo una volta al secondo
    };

    events.forEach(event => {
      document.addEventListener(event, throttledUpdateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledUpdateActivity);
      });
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [isAuthenticated, updateActivity]);

  return {
    sessionExpiresAt,
    isRefreshing,
    updateActivity,
    timeUntilExpiration: sessionExpiresAt ? (sessionExpiresAt instanceof Date ? sessionExpiresAt.getTime() : new Date(sessionExpiresAt).getTime()) - Date.now() : null
  };
};